import { Badge, Box, Stack, Text, TextInput, Textarea } from '@mantine/core'
import React from 'react'
import type { SpeakingQuestion } from 'src/apis/courseAiAPI'

/** A Basic UniVini Speaking lesson has exactly this many questions (the server refuses any other number). */
export const SPEAKING_QUESTION_COUNT = 5

/** A question as the form holds it: both inputs are text, so an empty hint is an empty string. */
export type SpeakingQuestionInput = { text: string; hint: string }

export const emptySpeakingQuestions = (): SpeakingQuestionInput[] =>
	Array.from({ length: SPEAKING_QUESTION_COUNT }, () => ({
		text: '',
		hint: '',
	}))

/** The stored questions as form inputs, always exactly 5 rows (a shorter list is padded with empty rows). */
export const questionsToInputs = (
	questions: unknown,
): SpeakingQuestionInput[] => {
	const list = Array.isArray(questions) ? questions : []
	return Array.from({ length: SPEAKING_QUESTION_COUNT }, (_, index) => {
		const item = list[index] as SpeakingQuestion | string | undefined
		if (typeof item === 'string') return { text: item, hint: '' }
		return { text: item?.text || '', hint: item?.hint || '' }
	})
}

/** The 5 questions as the server takes them: trimmed text, and a hint only when there is one. */
export const inputsToQuestions = (
	inputs: SpeakingQuestionInput[],
): SpeakingQuestion[] =>
	inputs.map((item) => {
		const text = item.text.trim()
		const hint = item.hint.trim()
		return hint ? { text, hint } : { text }
	})

/** The message under the questions, or null when every one of the 5 has its text. */
export const questionsError = (
	inputs: SpeakingQuestionInput[],
): string | null => {
	if (inputs.length !== SPEAKING_QUESTION_COUNT) {
		return `A Speaking lesson has exactly ${SPEAKING_QUESTION_COUNT} questions`
	}
	const empty = inputs
		.map((item, index) => (item.text.trim() ? 0 : index + 1))
		.filter(Boolean)
	if (empty.length) {
		return `Question ${empty.join(', ')} ${empty.length === 1 ? 'is' : 'are'} empty. All ${SPEAKING_QUESTION_COUNT} questions are required.`
	}
	return null
}

type SpeakingLessonFieldsProps = {
	intro: string
	questions: SpeakingQuestionInput[]
	/** "Day 12" on an existing lesson; "Day 31" (the next free day) on a new one. Left out where there is no day. */
	dayLabel?: string
	introError?: React.ReactNode
	questionsError?: React.ReactNode
	onIntroChange: (value: string) => void
	onQuestionsChange: (value: SpeakingQuestionInput[]) => void
}

/**
 * The Speaking part of the lesson form (UD-671): the intro the learner reads first, the 5 questions with an
 * optional hint under each, and the day. The server gives a new lesson the next free day, so the day is shown
 * and not typed.
 */
function SpeakingLessonFields({
	intro,
	questions,
	dayLabel,
	introError,
	questionsError: questionsErrorText,
	onIntroChange,
	onQuestionsChange,
}: SpeakingLessonFieldsProps) {
	const change = (index: number, patch: Partial<SpeakingQuestionInput>) =>
		onQuestionsChange(
			questions.map((item, at) =>
				at === index ? { ...item, ...patch } : item,
			),
		)

	return (
		<Stack spacing="sm">
			{dayLabel ? (
				<Text size="sm">
					<strong>Day index:</strong>{' '}
					<Badge variant="light" color="green" data-testid="speaking-day">
						{dayLabel}
					</Badge>
				</Text>
			) : null}
			<Textarea
				label="Intro"
				description="One or two sentences the learner reads before the first question"
				placeholder="Family is important. Let us talk about yours."
				minRows={2}
				autosize
				value={intro}
				error={introError}
				onChange={(event) => onIntroChange(event.currentTarget.value)}
			/>
			<Stack spacing={8} data-testid="speaking-questions">
				<Text size="sm" fw={600}>
					Questions ({SPEAKING_QUESTION_COUNT})
				</Text>
				{questions.map((item, index) => (
					<Box
						key={index}
						sx={{
							border: '1px solid #e6ebe7',
							borderRadius: 8,
							background: '#fafcfa',
							padding: '8px 10px',
						}}
					>
						<TextInput
							withAsterisk
							label={`Question ${index + 1}`}
							placeholder="Do you like street food?"
							value={item.text}
							onChange={(event) =>
								change(index, { text: event.currentTarget.value })
							}
						/>
						<TextInput
							mt={6}
							size="xs"
							label="Hint (optional)"
							placeholder="I think ... because ..."
							value={item.hint}
							onChange={(event) =>
								change(index, { hint: event.currentTarget.value })
							}
						/>
					</Box>
				))}
				{questionsErrorText ? (
					<Text size="xs" color="red" role="alert">
						{questionsErrorText}
					</Text>
				) : null}
			</Stack>
		</Stack>
	)
}

export default SpeakingLessonFields
